import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { currencies } from "../../../utilities/RequestData";
import {
  fetchExecutiveRequestDetails,
  updateExecutiveRequest,
  submitExecutiveRequest,
} from "../../../../contents/redux/actions/executiveRequestActions";
import {
  MonetizationOnRounded,
  PaymentsRounded,
  LiveHelpRounded,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { toast } from "react-toastify";
import styles from "../../../utilities/cssmodules/EditExecutiveRequest.module.css";

const EditExecutiveRequest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { requestId } = location.state || {};
  const userId = localStorage.getItem("userId");
  const dispatch = useDispatch();
  const [inputs, setInputs] = useState({
    amount: "",
    currency: "",
    purpose: "",
  });
  const [isFormValid, setIsFormValid] = useState(false);

  // Redux state
  const {
    executiveDetails,
    isLoading: executiveLoading,
    error: executiveError,
  } = useSelector((state) => state.executiveRequest || {});

  // Go back to previous component
  const goBack = () => {
    navigate(-1);
  };

  // Fetch request details
  useEffect(() => {
    if (!requestId) {
      toast.error("Request ID is missing.");
      navigate("/myrequests");
      return;
    }
    dispatch(fetchExecutiveRequestDetails(requestId));
  }, [dispatch, requestId, navigate]);

  // Prepopulate form with executiveDetails
  useEffect(() => {
    if (executiveDetails) {
      console.log(
        "Prepopulating form with executiveDetails:",
        executiveDetails,
      );
      setInputs({
        amount: executiveDetails.amount?.toString() || "",
        currency: executiveDetails.currency || "",
        purpose: executiveDetails.purpose || "",
      });
    }
  }, [executiveDetails]);

  // Validate form
  useEffect(() => {
    const isValid =
      inputs.amount &&
      !isNaN(parseFloat(inputs.amount)) &&
      parseFloat(inputs.amount) > 0 &&
      inputs.currency &&
      inputs.purpose.trim();
    setIsFormValid(isValid);
  }, [inputs]);

  // Handle input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Validate and save form
  const handleSave = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }
    if (!requestId) {
      toast.error("Request ID is missing.");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }
    if (!executiveDetails?.requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }

    const requestData = {
      id: parseInt(requestId),
      currency: inputs.currency,
      amount: parseFloat(inputs.amount),
      purpose: inputs.purpose.trim(),
      userId: parseInt(userId),
      isOnSaved: true,
      requestType: parseInt(executiveDetails.requestTypeId),
    };

    dispatch(updateExecutiveRequest(requestId, requestData))
      .then(() => {
        toast.success("Executive request saved successfully.");
        navigate("/myrequests");
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join("; ")
          : error.response?.data?.message || error.message;
        toast.error(`Failed to save executive request: ${errorMessage}`);
      });
  };

  // Validate and submit form
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }
    if (!requestId) {
      toast.error("Request ID is missing.");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }
    if (!executiveDetails?.requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }

    const requestData = {
      id: parseInt(requestId),
      currency: inputs.currency,
      amount: parseFloat(inputs.amount),
      purpose: inputs.purpose.trim(),
      userId: parseInt(userId),
      isOnSaved: false,
      requestType: parseInt(executiveDetails.requestTypeId),
    };

    dispatch(updateExecutiveRequest(requestId, requestData))
      .then(() => {
        dispatch(submitExecutiveRequest(requestId));
      })
      .then(() => {
        toast.success("Executive request submitted successfully.");
        navigate("/myrequests");
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join("; ")
          : error.response?.data?.message || error.message;
        toast.error(`Failed to submit executive request: ${errorMessage}`);
      });
  };

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["component-title"]}>
        <div className={styles["flex-row-container"]}>
          <div className={styles["flex-row-custom-1"]}>
            <span
              className={styles["title-back-button"]}
              onClick={goBack}
              aria-label="Go back to previous page"
            >
              <Tooltip title="Go Back" arrow placement="top">
                <ArrowBackIosIcon />
              </Tooltip>
            </span>
            <span>Edit Executive Request</span>
          </div>
        </div>
      </div>
      <div className={styles["generic-activity-component-description"]}>
        Edit your executive request by updating the form below and submitting
        when you are done. Ensure all fields have accurate information.
      </div>
      {executiveError && (
        <div className={styles["error-message"]}>
          {executiveError.includes("404")
            ? "Request not found."
            : executiveError}
        </div>
      )}
      {executiveLoading && (
        <div className={styles["loading-message"]}>
          Loading request details...
        </div>
      )}
      {!executiveLoading && !executiveDetails && !executiveError && (
        <div className={styles["error-message"]}>Request details not found</div>
      )}
      <div className={styles["generic_request_content_component_wrapper"]}>
        <form
          className={styles["generic-activity-component-form"]}
          onSubmit={handleSubmit}
        >
          <label className={styles["form-label"]}>
            <MonetizationOnRounded /> Amount
          </label>
          <section className={styles["form-section"]}>
            <input
              type="number"
              name="amount"
              placeholder="Enter amount"
              required
              value={inputs.amount}
              onChange={handleChange}
              min="0"
              step="0.01"
              className={styles["form-input"]}
              aria-label="Amount"
            />
          </section>

          <label className={styles["form-label"]}>
            <PaymentsRounded /> Currency
          </label>
          <section className={styles["form-section"]}>
            <select
              name="currency"
              required
              value={inputs.currency}
              onChange={handleChange}
              className={styles["form-select"]}
              aria-label="Currency"
            >
              <option value="">Select a currency</option>
              {currencies.map((currency) => (
                <option key={currency.id} value={currency.name}>
                  {currency.name}
                </option>
              ))}
            </select>
          </section>

          <label className={styles["form-label"]}>
            <LiveHelpRounded /> Purpose
          </label>
          <section className={styles["form-section"]}>
            <input
              type="text"
              name="purpose"
              placeholder="Enter purpose"
              required
              value={inputs.purpose}
              onChange={handleChange}
              maxLength="100"
              className={styles["form-input"]}
              aria-label="Purpose"
            />
          </section>

          <div className={styles["button-row"]}>
            <button
              type="button"
              className={styles["generic-save-btn"]}
              onClick={handleSave}
              disabled={!isFormValid || executiveLoading}
              aria-label="Save executive request"
            >
              Save
            </button>
            <button
              type="button"
              className={styles["generic-submit-btn"]}
              onClick={handleSubmit}
              disabled={!isFormValid || executiveLoading}
              aria-label="Submit executive request"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditExecutiveRequest;
